/** Alineado con `backend/inventory/recommendation_catalog.py` y `pruebas/Casos Inventory Health(Casos Uso).csv`. */

export type RecommendationColorVariant = 'danger' | 'warning' | 'success' | 'neutral';

export const FALLBACK_CATEGORY_KEY = 'revisar_manualmente';

const CATEGORY_SORT: Record<string, { sortOrder: number; color: RecommendationColorVariant }> = {
  compra_urgente: { sortOrder: 5, color: 'danger' },
  reponer: { sortOrder: 10, color: 'success' },
  no_reponer: { sortOrder: 15, color: 'warning' },
  inconsistencia: { sortOrder: 20, color: 'danger' },
  revisar_reposicion: { sortOrder: 25, color: 'danger' },
  revisar_parametros: { sortOrder: 30, color: 'warning' },
  revisar_minimo: { sortOrder: 35, color: 'warning' },
  monitorear_transito: { sortOrder: 40, color: 'warning' },
  revisar_transito: { sortOrder: 45, color: 'warning' },
  congelar_compras: { sortOrder: 50, color: 'warning' },
  liquidar_stock: { sortOrder: 55, color: 'danger' },
  liquidar_obsoleto: { sortOrder: 60, color: 'danger' },
  liquidar_o_reutilizar: { sortOrder: 65, color: 'danger' },
  validar_baja: { sortOrder: 70, color: 'danger' },
  monitorear_obsoleto: { sortOrder: 75, color: 'warning' },
  monitorear_forecast: { sortOrder: 80, color: 'warning' },
  validar_datos: { sortOrder: 85, color: 'warning' },
  validar_forecast: { sortOrder: 90, color: 'warning' },
  candidato_stock: { sortOrder: 95, color: 'warning' },
  mantener_bajo_pedido: { sortOrder: 100, color: 'warning' },
  bajo_pedido_lead_time_corto: { sortOrder: 105, color: 'warning' },
  sin_accion: { sortOrder: 110, color: 'success' },
  revisar_manualmente: { sortOrder: 115, color: 'neutral' },
};

const COLOR_VARIANT_SORT: Record<RecommendationColorVariant, number> = {
  danger: 0,
  warning: 1,
  success: 2,
  neutral: 3,
};

export type RecommendationBarSortable = {
  category_key: string;
  sort_order: number;
  color_variant: RecommendationColorVariant;
};

/** Gráfico: agrupa por color (rojo → amarillo → verde → gris), luego prioridad de negocio. */
export function compareRecommendationBarsByColor(
  a: RecommendationBarSortable,
  b: RecommendationBarSortable,
): number {
  const colorCmp =
    COLOR_VARIANT_SORT[a.color_variant] - COLOR_VARIANT_SORT[b.color_variant];
  if (colorCmp !== 0) return colorCmp;
  const orderCmp = a.sort_order - b.sort_order;
  if (orderCmp !== 0) return orderCmp;
  return a.category_key.localeCompare(b.category_key);
}

const ACTION_TO_CATEGORY: Record<string, string> = {
  ON_DEMAND_RECURRENTE_REVISAR_PASAR_A_STOCK: 'candidato_stock',
  ON_DEMAND_RECURRENTE_PERO_CARO_MANTENER_BAJO_PEDIDO: 'mantener_bajo_pedido',
  ON_DEMAND_RECURRENTE_PERO_LEAD_TIME_CORTO_MANTENER_BAJO_PEDIDO: 'bajo_pedido_lead_time_corto',
  SIN_SUGERENCIA_ACTIVA_RECALCULAR_MRP: 'sin_accion',
  SIN_STOCK_MRP_SUGIERE_COMPRA_URGENTE: 'compra_urgente',
  SIN_STOCK_CON_FORECAST_Y_TRANSITO_MONITOREAR_RECEPCION: 'monitorear_transito',
  SIN_STOCK_CON_DEMANDA_RECIENTE_Y_TRANSITO_MONITOREAR_RECEPCION: 'monitorear_transito',
  SIN_STOCK_CON_FORECAST_PERO_MRP_NO_SUGIERE_REVISAR_POLITICA: 'revisar_reposicion',
  SIN_STOCK_CON_DEMANDA_RECIENTE_PERO_MRP_NO_SUGIERE_REVISAR_POLITICA: 'revisar_reposicion',
  SIN_STOCK_PERO_MRP_INDICA_EXCESO_REVISAR_INCONSISTENCIA: 'inconsistencia',
  SIN_STOCK_SOLO_DEMANDA_HISTORICA_SIN_FORECAST_NO_REPONER_AUTOMATICAMENTE: 'no_reponer',
  NO_REPONER_REVISAR_OBSOLESCENCIA: 'no_reponer',
  MRP_SUGIERE_COMPRA_PERO_NO_HAY_RELACION_CON_VENTAS_REVISAR: 'validar_datos',
  SIN_CONSUMO_HISTORICO_PERO_CON_FORECAST_REVISAR: 'validar_forecast',
  SIN_RELACION_CON_MODELOS_ELEMENTOS_VENDIDOS: 'liquidar_o_reutilizar',
  EXCESO_SIN_RELACION_CON_MODELOS_ELEMENTOS_VENDIDOS: 'liquidar_o_reutilizar',
  REPONER: 'reponer',
  BAJO_MINIMO_CON_FORECAST_Y_TRANSITO_MONITOREAR_RECEPCION: 'monitorear_transito',
  BAJO_MINIMO_CON_DEMANDA_RECIENTE_Y_TRANSITO_MONITOREAR_RECEPCION: 'monitorear_transito',
  BAJO_MINIMO_CON_FORECAST_PERO_MRP_NO_SUGIERE_REVISAR_PARAMETROS: 'revisar_parametros',
  BAJO_MINIMO_CON_DEMANDA_RECIENTE_PERO_MRP_NO_SUGIERE_REVISAR_PARAMETROS: 'revisar_parametros',
  BAJO_MINIMO_PERO_MRP_INDICA_EXCESO_REVISAR_INCONSISTENCIA: 'inconsistencia',
  REVISAR_MINIMO_NO_JUSTIFICADO: 'revisar_minimo',
  EXCESO_PERO_MRP_SUGIERE_COMPRA_REVISAR_INCONSISTENCIA: 'inconsistencia',
  CONGELAR_COMPRAS_MONITORIZAR_CONSUMO: 'congelar_compras',
  EXCESO_CONFIRMADO_CON_DEMANDA_RECIENTE_O_FUTURA: 'congelar_compras',
  EXCESO_SIN_DEMANDA_RECIENTE_REVISAR_LIQUIDACION: 'liquidar_stock',
  OBSOLETO_PERO_MRP_SUGIERE_COMPRA_MONITOREAR_PARAMETROS: 'monitorear_obsoleto',
  OBSOLETO_CON_FORECAST_FUTURO_MONITOREAR: 'monitorear_forecast',
  OBSOLETO_CON_DEMANDA_ULTIMOS_12_MESES_MONITOREAR: 'monitorear_obsoleto',
  OBSOLETO_CON_EXCESO_SIN_DEMANDA_REVISAR_LIQUIDACION: 'liquidar_obsoleto',
  VALIDAR_BAJA_O_DESCATALOGAR: 'validar_baja',
  EN_TRANSITO_MONITORIZAR_RECEPCION: 'monitorear_transito',
  EN_TRANSITO_SIN_DEMANDA_RECIENTE_REVISAR: 'revisar_transito',
  OPTIMO_PERO_MRP_SUGIERE_COMPRA_REVISAR_STOCK_STATUS: 'sin_accion',
  OPTIMO_PERO_MRP_INDICA_EXCESO_REVISAR_STOCK_STATUS: 'sin_accion',
  SIN_ACCION_INMEDIATA: 'sin_accion',
  REVISAR_MANUALMENTE: 'revisar_manualmente',
};

export const ON_DEMAND_BAR_COLOR = '#1681ba';
export const ON_DEMAND_BAR_COLOR_ACTIVE = '#0e6a9a';
export const STOCK_BAR_COLOR = '#b8c5d6';
export const STOCK_BAR_COLOR_ACTIVE = '#64748b';

export function categoryAccentColor(variant: RecommendationColorVariant): string {
  switch (variant) {
    case 'danger':
      return '#EF4444';
    case 'warning':
      return '#EAB308';
    case 'success':
      return '#22C55E';
    default:
      return '#9CA3AF';
  }
}

function mixHexWithWhite(hex: string, whiteRatio: number): string {
  const normalized = hex.replace('#', '');
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const mix = (channel: number) =>
    Math.round(channel * (1 - whiteRatio) + 255 * whiteRatio);
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

/** Fabbisogno = tono fuerte; Scorta = tono claro del color de la etiqueta. */
export function categoryBarSegmentColors(variant: RecommendationColorVariant): {
  onDemandStrong: string;
  stockLight: string;
} {
  const strong = categoryAccentColor(variant);
  return {
    onDemandStrong: strong,
    stockLight: mixHexWithWhite(strong, 0.58),
  };
}

export function resolveCategoryMeta(
  recommendedAction: string | null | undefined,
  backendCategoryKey?: string | null,
  backendColor?: RecommendationColorVariant | null,
): { categoryKey: string; color: RecommendationColorVariant } {
  if (backendCategoryKey && backendColor) {
    return { categoryKey: backendCategoryKey, color: backendColor };
  }
  if (!recommendedAction?.trim()) {
    return { categoryKey: FALLBACK_CATEGORY_KEY, color: 'neutral' };
  }
  const categoryKey = ACTION_TO_CATEGORY[recommendedAction.trim()] ?? FALLBACK_CATEGORY_KEY;
  const meta = CATEGORY_SORT[categoryKey] ?? { sortOrder: 115, color: 'neutral' as const };
  return { categoryKey, color: meta.color };
}

export function categoryBadgeClass(color: RecommendationColorVariant): string {
  switch (color) {
    case 'danger':
      return 'bg-red-100 text-red-800';
    case 'warning':
      return 'bg-yellow-100 text-yellow-800';
    case 'success':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export function translateCategoryLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  categoryKey: string,
): string {
  const key = `inventoryPolicies.recommendations.categories.${categoryKey}`;
  const translated = t(key);
  return translated === key ? categoryKey : translated;
}

export function translateActionLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  recommendedAction: string | null | undefined,
): string {
  if (!recommendedAction?.trim()) return '—';
  const key = `inventoryPolicies.recommendations.actions.${recommendedAction.trim()}`;
  const translated = t(key);
  return translated === key ? recommendedAction : translated;
}
